// components/checkout/PaymentMethod.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaCreditCard,
    FaGoogle,
    FaLock,
    FaCheckCircle,
    FaCcVisa,
    FaCcMastercard,
    FaCcAmex,
} from 'react-icons/fa';
import LuxuryInput from '../ui/LuxuryInput';
import { formReveal } from "../animations/checkoutAnimations";
import {SiRazorpay} from "react-icons/si";
import {FaIndianRupeeSign} from "react-icons/fa6";

const PaymentMethod = ({ method, onSelect, cardDetails, onCardChange, errors = {}, methods = [] }) => {
    const [saveCard, setSaveCard] = useState(false);

    // Map method IDs to icons
    const getIconForMethod = (methodId) => {
        switch(methodId) {
            case 'card': return FaCreditCard;
            case 'razorpay': return SiRazorpay;
            case 'gpay': return FaGoogle;
            default: return FaCreditCard;
        }
    };

    const getDescriptionForMethod = (methodId) => {
        switch(methodId) {
            case 'card': return 'Pay securely with your card';
            case 'razorpay': return 'UPI, Net Banking, Wallet';
            case 'gpay': return 'Quick tap to pay';
            default: return 'Secure payment';
        }
    };

    // Default methods if none provided
    const paymentMethods = methods.length > 0 ? methods.map(m => ({
        id: m.id,
        provider: m.provider,
        name: m.name,
        icon: getIconForMethod(m.id),
        description: getDescriptionForMethod(m.id),
        supportedCards: m.id === 'card' ? [FaCcVisa, FaCcMastercard, FaCcAmex] : null,
        minAmount: m.minAmount || 0,
        maxAmount: m.maxAmount || 1000000
    })) : [
        {
            id: 'card',
            provider: 'stripe',
            name: 'Credit / Debit Card',
            icon: FaCreditCard,
            description: 'Pay securely with your card',
            supportedCards: [FaCcVisa, FaCcMastercard, FaCcAmex],
            minAmount: 50,
            maxAmount: 500000,
            popular: true
        },
        {
            id: 'razorpay',
            provider: 'razorpay',
            name: 'Razorpay UPI',
            icon: SiRazorpay,
            description: 'UPI, Net Banking, Wallet',
            minAmount: 1,
            maxAmount: 1000000
        },
        {
            id: 'gpay',
            provider: 'gpay',
            name: 'Google Pay',
            icon: FaGoogle,
            description: 'Quick tap to pay',
            minAmount: 10,
            maxAmount: 100000
        }
    ];

    return (
        <motion.div
            variants={formReveal}
            className="space-y-6"
        >
            {/* Payment Methods */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {paymentMethods.map((paymentMethod, index) => (
                    <motion.button
                        key={paymentMethod.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => onSelect(paymentMethod.id)}
                        className={`
                            p-4 rounded-2xl flex flex-col items-center justify-center gap-2
                            transition-all duration-300 relative
                            ${method === paymentMethod.id
                            ? 'border-2 border-champagne bg-gradient-to-br from-champagne/5 to-transparent card-selected'
                            : 'border border-nude hover:border-champagne/50 hover:bg-nude/5'
                        }
                        `}
                    >
                        <div className={`
                            w-12 h-12 rounded-full flex items-center justify-center mb-2
                            ${method === paymentMethod.id
                            ? 'bg-gradient-to-br from-champagne to-champagne/80 text-white'
                            : 'bg-nude/30 text-charcoal/50'
                        }
                        `}>
                            <paymentMethod.icon className="w-5 h-5" />
                        </div>

                        <div className="text-center">
                            <h4 className="font-medium text-charcoal text-sm mb-1">{paymentMethod.name}</h4>
                            <p className="text-xs text-charcoal/50">{paymentMethod.description}</p>
                        </div>

                        {paymentMethod.popular && method !== paymentMethod.id && (
                            <span className="text-xs px-2 py-1 bg-champagne/10 text-champagne rounded-full mt-2">
                                Popular
                            </span>
                        )}

                        {method === paymentMethod.id && (
                            <FaCheckCircle className="w-4 h-4 text-sage absolute top-2 right-2" />
                        )}

                        {/* Supported Cards */}
                        {paymentMethod.supportedCards && (
                            <div className="flex gap-1 mt-2">
                                {paymentMethod.supportedCards.map((CardIcon, idx) => (
                                    <CardIcon key={idx} className="w-4 h-4 text-charcoal/30" />
                                ))}
                            </div>
                        )}
                    </motion.button>
                ))}
            </div>

            {/* Card Details Form */}
            <AnimatePresence>
                {method === 'card' && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-4 p-6 rounded-2xl border border-nude/50 bg-gradient-to-br from-pearl to-transparent"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <FaLock className="w-5 h-5 text-sage" />
                            <div>
                                <h3 className="font-medium text-charcoal">Secure Card Payment</h3>
                                <p className="text-sm text-charcoal/50">Your payment is encrypted and secure</p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <LuxuryInput
                                label="Card Number"
                                value={cardDetails.number}
                                onChange={(e) => {
                                    let value = e.target.value.replace(/\D/g, '');
                                    if (value.length > 16) value = value.slice(0, 16);
                                    value = value.replace(/(.{4})/g, '$1 ').trim();
                                    onCardChange({...cardDetails, number: value});
                                }}
                                placeholder="1234 5678 9012 3456"
                                error={errors.number}
                                required
                                size="large"
                            />

                            <LuxuryInput
                                label="Cardholder Name"
                                value={cardDetails.name}
                                onChange={(e) => onCardChange({...cardDetails, name: e.target.value})}
                                placeholder="John Doe"
                                error={errors.name}
                                required
                                size="large"
                            />
                        </div>

                        <div className="grid md:grid-cols-3 gap-4">
                            <LuxuryInput
                                label="Expiry Date"
                                value={cardDetails.expiry}
                                onChange={(e) => {
                                    let value = e.target.value.replace(/\D/g, '');
                                    if (value.length > 4) value = value.slice(0, 4);
                                    if (value.length >= 2) {
                                        value = value.slice(0,2) + '/' + value.slice(2);
                                    }
                                    onCardChange({...cardDetails, expiry: value});
                                }}
                                placeholder="MM/YY"
                                error={errors.expiry}
                                required
                                size="large"
                            />

                            <LuxuryInput
                                label="CVV"
                                value={cardDetails.cvv}
                                onChange={(e) => {
                                    let value = e.target.value.replace(/\D/g, '');
                                    if (value.length > 4) value = value.slice(0, 4);
                                    onCardChange({...cardDetails, cvv: value});
                                }}
                                placeholder="123"
                                type="password"
                                error={errors.cvv}
                                required
                                size="large"
                            />

                            <div className="flex items-end">
                                <label className="flex items-center gap-2 cursor-pointer h-full">
                                    <input
                                        type="checkbox"
                                        checked={saveCard}
                                        onChange={(e) => setSaveCard(e.target.checked)}
                                        className="w-4 h-4 rounded border-nude text-champagne focus:ring-champagne/30"
                                    />
                                    <span className="text-sm text-charcoal/70">Save card securely</span>
                                </label>
                            </div>
                        </div>

                        {/* Security Badges */}
                        <div className="pt-4 border-t border-nude/30">
                            <div className="flex flex-wrap items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-sage/10 flex items-center justify-center">
                                        <FaLock className="w-3 h-3 text-sage" />
                                    </div>
                                    <span className="text-xs text-charcoal/50">256-bit SSL</span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-sage/10 flex items-center justify-center">
                                        <FaCheckCircle className="w-3 h-3 text-sage" />
                                    </div>
                                    <span className="text-xs text-charcoal/50">PCI DSS compliant</span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-sage/10 flex items-center justify-center">
                                        <FaIndianRupeeSign className="w-3 h-3 text-sage" />
                                    </div>
                                    <span className="text-xs text-charcoal/50">INR payments</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Payment Method Info */}
            <AnimatePresence>
                {method === 'razorpay' && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="p-6 rounded-2xl border border-nude/50 bg-gradient-to-br from-pearl to-transparent"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <SiRazorpay  className="w-5 h-5 text-sage" />
                            <div>
                                <h3 className="font-medium text-charcoal">Razorpay Payment</h3>
                                <p className="text-sm text-charcoal/50">Pay via UPI, Net Banking, or Wallets</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {['UPI', 'Net Banking', 'Credit Card', 'Debit Card'].map((option, idx) => (
                                    <div key={idx} className="p-3 rounded-lg border border-nude/30 text-center">
                                        <p className="text-sm font-medium text-charcoal">{option}</p>
                                    </div>
                                ))}
                            </div>

                            <p className="text-sm text-charcoal/60">
                                You will be redirected to Razorpay's secure payment page to complete your transaction.
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {method === 'gpay' && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="p-6 rounded-2xl border border-nude/50 bg-gradient-to-br from-pearl to-transparent"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <FaGoogle className="w-5 h-5 text-sage" />
                            <div>
                                <h3 className="font-medium text-charcoal">Google Pay</h3>
                                <p className="text-sm text-charcoal/50">Fast and secure payments</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-blue-100/50">
                                <p className="text-sm text-charcoal/70">
                                    Click "Place Order" to proceed with Google Pay. You'll be redirected to complete the payment.
                                </p>
                            </div>

                            <div className="flex items-center gap-2 text-sm text-charcoal/50">
                                <FaCheckCircle className="w-4 h-4 text-sage" />
                                <span>Secured by Google Pay</span>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Trust Message */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="p-6 rounded-2xl bg-gradient-to-r from-champagne/5 to-sage/5"
            >
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                        <FaLock className="w-5 h-5 text-champagne" />
                    </div>
                    <div>
                        <h4 className="font-medium text-charcoal mb-2">Your payment is secure</h4>
                        <p className="text-sm text-charcoal/60 mb-3">
                            All payments are processed through secure payment gateways with bank-level encryption.
                            Your payment details are never stored on our servers.
                        </p>
                        <p className="text-xs text-charcoal/40">
                            By continuing, you agree to our Terms of Service and Privacy Policy.
                        </p>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default PaymentMethod;